import React from 'react';
import './NotificationBadge.css';

interface NotificationBadgeProps {
  /** Numeric count to display. Badge is hidden when 0/undefined unless `dot` is set. */
  count?: number;
  /** Render a small dot with no number (e.g. unread indicator). */
  dot?: boolean;
  /** Semantic color variant. Colors flow from theme tokens. */
  variant?: 'danger' | 'warning' | 'info';
  /** Cap displayed count, e.g. max=99 renders "99+" for higher values. */
  max?: number;
  className?: string;
  'aria-label'?: string;
}

const NotificationBadge: React.FC<NotificationBadgeProps> = ({
  count,
  dot = false,
  variant = 'danger',
  max,
  className = '',
  'aria-label': ariaLabel,
}) => {
  // Dot mode: always render, no number.
  if (dot) {
    const classNames = [
      'notification-badge',
      `notification-badge--${variant}`,
      'notification-badge--dot',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return <span className={classNames} role="status" aria-label={ariaLabel} />;
  }

  // Count mode: hide when 0 or undefined.
  if (!count || count <= 0) return null;

  const display = max && count > max ? `${max}+` : String(count);

  const classNames = [
    'notification-badge',
    `notification-badge--${variant}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={classNames} aria-label={ariaLabel}>
      {display}
    </span>
  );
};

export default NotificationBadge;
